import { ChangeDetectionStrategy, Component, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { catchError, finalize, of, take } from 'rxjs';
import { NgIf, NgFor, CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CustomizerSettingsService } from '../../../customizer-settings/customizer-settings.service';
import { BaseSearchOptions } from '../../../models/core/table/BaseSearchOptions';
import { ZekvoListViewDto } from '../../../models/domain/ZekvoListViewDto';
import { ModalText } from '../../../models/enums/ModalText';
import { RoleEnum } from '../../../models/enums/RoleEnum';
import { AuthenticationService } from '../../../services/authentication.service';
import { LoadingService } from '../../../services/loading.service';
import { ModalService } from '../../../services/modal.service';
import { ZekvoService } from '../../../services/zekvo.service';
import { UrlHelper } from '../../../utilities/UrlHelper';
import { ZekvoDetailsComponent } from '../zekvo-details/zekvo-details.component';

@Component({
    selector: 'app-zekvo-list',
    imports: [RouterLink, NgIf, NgFor, CommonModule, FormsModule, ZekvoDetailsComponent],
    templateUrl: './zekvo-list.component.html',
    styleUrl: './zekvo-list.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ZekvoListComponent {
    products = signal<ZekvoListViewDto[]>([]);
    selectedIds = signal<number[]>([]);

    productNameFilter = signal<string>('');
    productCodeFilter = signal<string>('');

    pageIndex = 0;
    pageSize = 50;
    totalRecords = 0;

    RoleEnum = RoleEnum;
    ModalText = ModalText;

    isToggled = false;
    classApplied = false;
    zekvoId = 0;

    showImageModal = false;
    selectedImageUrl = '';
    selectedImageTitle = '';

    constructor(
        public themeService: CustomizerSettingsService,
        public zekvoService: ZekvoService,
        private modalService: ModalService,
        private toasterService: ToastrService,
        private loadingService: LoadingService,
        public authService: AuthenticationService
    ) {
        this.themeService.isToggled$.subscribe(isToggled => {
            this.isToggled = isToggled;
        });
    }

    ngOnInit(): void {
        this.loadData();
    }

    private loadData(): void {
        const searchOptions = new BaseSearchOptions(this.buildFilter(), 'productName', 0, this.pageIndex, this.pageSize);
        this.loadingService.showLoading();
        this.zekvoService.get(searchOptions).pipe(
            take(1),
            catchError((error) => {
                if (error?.error?.isCustom) {
                    this.toasterService.warning(error.error.message);
                } else {
                    this.toasterService.error('Failed to load Zekvo products');
                }
                return of(null);
            }),
            finalize(() => this.loadingService.hideLoading())
        ).subscribe(result => {
            if (result?.body?.values) {
                this.products.set(result.body.values as unknown as ZekvoListViewDto[]);
                this.totalRecords = result.body.totalCount ?? 0;
            }
        });
    }

    private buildFilter(): string | undefined {
        const parts: string[] = [];
        const name = this.productNameFilter().trim();
        const code = this.productCodeFilter().trim();
        if (name) parts.push(`productName@=*${name}`);
        if (code) parts.push(`productCode@=*${code}`);
        return parts.length ? parts.join(',') : undefined;
    }

    public applyFilters(): void {
        this.pageIndex = 0;
        this.loadData();
    }

    public clearFilters(): void {
        this.productNameFilter.set('');
        this.productCodeFilter.set('');
        this.pageIndex = 0;
        this.loadData();
    }

    public toggleSelection(id: number): void {
        const current = this.selectedIds();
        if (current.includes(id)) {
            this.selectedIds.set(current.filter(x => x !== id));
        } else {
            this.selectedIds.set([...current, id]);
        }
    }

    public isSelected(id: number): boolean {
        return this.selectedIds().includes(id);
    }

    public toggleSelectAll(): void {
        if (this.selectedIds().length === this.products().length) {
            this.selectedIds.set([]);
        } else {
            this.selectedIds.set(this.products().map(p => p.id!).filter(Boolean));
        }
    }

    public get allSelected(): boolean {
        const items = this.products();
        return items.length > 0 && this.selectedIds().length === items.length;
    }

    public onAddClick(): void {
        this.zekvoId = 0;
        this.classApplied = true;
    }

    public onEditClick(event: MouseEvent, data: ZekvoListViewDto): void {
        event.stopPropagation();
        this.zekvoId = data.id!;
        this.classApplied = true;
    }

    public onDeleteClick(event: MouseEvent, data: ZekvoListViewDto): void {
        event.stopPropagation();
        this.modalService.openModal(
            'Delete Confirmation',
            `Are you sure you want to delete <strong>${data.productName}</strong>?`,
            ModalText.ConfirmYes,
            ModalText.ConfirmNo
        );

        this.modalService.onConfirm.subscribe((isConfirm) => {
            if (isConfirm && data.id) {
                this.loadingService.showLoading();
                this.zekvoService.delete(data.id).pipe(
                    take(1),
                    catchError((error) => {
                        if (error?.error?.isCustom) {
                            this.toasterService.warning(error.error.message);
                        } else {
                            this.toasterService.error('Failed to delete');
                        }
                        return of(null);
                    }),
                    finalize(() => this.loadingService.hideLoading())
                ).subscribe(result => {
                    if (result?.body) {
                        this.toasterService.info('Record deleted successfully.');
                        this.selectedIds.set(this.selectedIds().filter(id => id !== data.id));
                        this.loadData();
                    }
                });
            }
        });
    }

    public exportSelectedToPdf(): void {
        const ids = this.selectedIds();
        if (ids.length === 0) {
            this.toasterService.warning('Please select at least one product to export.');
            return;
        }

        this.loadingService.showLoading();
        this.zekvoService.generatePdfByZekvoIds(ids).pipe(
            take(1),
            catchError((error) => {
                if (error?.error?.isCustom) {
                    this.toasterService.warning(error.error.message);
                } else {
                    this.toasterService.error('Failed to generate PDF');
                }
                return of(null);
            }),
            finalize(() => this.loadingService.hideLoading())
        ).subscribe(response => {
            if (response?.body) {
                const filename = this.extractFilename(response) || 'zekvo-products.pdf';
                this.downloadBlob(response.body, filename);
                this.toasterService.success('PDF generated successfully');
            }
        });
    }

    private extractFilename(response: any): string | null {
        const cd = response.headers.get('content-disposition');
        if (!cd) return null;
        const m = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(cd);
        if (m && m[1]) {
            let f = m[1].replace(/['"]/g, '');
            if (f.includes("UTF-8''")) f = decodeURIComponent(f.split("UTF-8''")[1]);
            return f;
        }
        return null;
    }

    private downloadBlob(blob: Blob, filename: string): void {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }

    public classAppliedEvent(flag: boolean): void {
        this.classApplied = flag;
    }

    public saveConfirmationEvent(isSaved: boolean): void {
        if (isSaved) this.loadData();
    }

    public getImageUrl(path: string): string {
        return UrlHelper.getImageUrl(path);
    }

    public onImageClick(event: MouseEvent, data: ZekvoListViewDto): void {
        event.stopPropagation();
        this.selectedImageUrl = UrlHelper.getImageUrl(data.photoUrl);
        this.selectedImageTitle = `${data.productName}${data.productCode ? ' - ' + data.productCode : ''}`;
        this.showImageModal = true;
    }

    public closeImageModal(): void {
        this.showImageModal = false;
        this.selectedImageUrl = '';
        this.selectedImageTitle = '';
    }
}
